<template>
    <div>
        <label
            v-if="label"
            class="title bold static-label"
            :for="id">
            <span>{{ label }}</span>
        </label>
        <component
            :is="tag"
            :class="[
                'btn',
                `btn--${variant}`,
                { 'btn--floating': floating },
                { 'btn--auto': auto }
            ]"
            :href="href"
            :data-theme="theme"
            :disabled="disabled"
        >

            <template>
                <i
                    v-if="floating"
                    class="i-plus" />
                <i
                    v-if="icon && iconPosition == 'left'"
                    :class="`i-${icon} pos-${iconPosition}`"
                />
                <slot />
                <i
                    v-if="icon && iconPosition == 'right'"
                    :class="`i-${icon} pos-${iconPosition}`"
                />

                <i
                    v-if="loading"
                    class="i-load spinning" />
            </template>
        </component>
    </div>
</template>

<script>
export default {
    name: 'CoreButton',
    props: {
        tag: {
            type: String,
            default: 'button'
        },
        href: {
            type: [String, Boolean],
            default: false
        },
        variant: {
            type: String,
            default: 'filled'
        },
        label: {
            type: String,
            default: null
        },
        theme: {
            type: String,
            default: 'primary'
        },
        icon: {
            type: String,
            default: ''
        },
        iconPosition: {
            type: String,
            default: 'right'
        },
        loading: {
            type: Boolean,
            default: false
        },
        disabled: {
            type: Boolean,
            default: false
        },
        floating: {
            type: Boolean,
            default: false
        },
        auto: {
            type: Boolean,
            default: false
        }
    }
};
</script>

<style lang="scss" scoped>
.btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 1rem;
    border-radius: 4px;
    font-size: 0.875rem;
    font-weight: bold;
    line-height: 1rem;
    cursor: pointer;

    @extend %btn-variants;

    &:disabled {
        opacity: 0.5;
        pointer-events: none;
    }

    &.fill {
        justify-content: center;
        width: 100%;
    }

    span {
        & + i {
            &.pos-left {
                margin-right: 1rem;
            }

            &.pos-right {
                margin-left: 1rem;
            }
        }
    }

    span + i.i-load {
        margin-left: 1rem;
    }

    i {
        font-size: 0.875rem;

        & + span {
            margin-left: 1rem;
        }
    }

    &.btn--floating {
        position: fixed;
        right: 2rem;
        bottom: 2rem;
        width: 3rem;
        min-width: auto !important;
        height: 3rem;
        border-radius: 50%;
    }
}

.static-label {
    display: block;
    margin-bottom: 0.5rem;
    color: var(--text-dark);
}

%btn-variants {
    &.btn--filled,
    &.btn--outline,
    &:not(btn--floating) {
        min-width: 200px;
    }

    &.btn--auto {
        min-width: auto !important;
    }

    &.btn--filled {
        @include transition(0.3s background-color);

        &[data-theme='primary'] {
            background-color: var(--button-primary);
            color: var(--button-text-primary);

            &:hover {
                background-color: var(--button-primary-dark);
            }
        }

        &[data-theme='secondary'] {
            @extend %btn-theme-blue;
        }

        &[data-theme='danger'] {
            @extend %btn-theme-red;
        }

        &[data-theme='light'] {
            @extend %btn-theme-grey;
        }
    }

    &.btn--outline {
        &[data-theme='primary'] {
            border: 1px solid var(--ui-grey);
            background-color: var(--bg-white);
            color: var(--primary);

            &:hover {
                border-color: var(--primary);
                background-color: var(--primary);
                color: #fff;
            }

            &:disabled {
                background-color: var(--grey);
                color: var(--text-white);
            }
        }
    }

    &.btn--icon {
        &[data-theme='primary'] {
            &:hover {
                background-color: var(--secondary);
            }

            @extend %btn-theme-orange;

            &:disabled {
                background-color: var(--grey);
            }
        }
    }
}

%btn-theme-orange {
    background-color: var(--primary);
    color: var(--text-white);

    &:hover {
        background-color: var(--primary-dark);
    }
}

%btn-theme-blue {
    background-color: var(--secondary);
    color: var(--text-white);
}

%btn-theme-grey {
    background-color: var(--ui-grey);
    color: #737373;
}

%btn-theme-red {
    background-color: var(--error);
    color: var(--text-white);
}

</style>
